// src/components/layout/ModaleRoute.tsx — SCE Issues
// Enveloppe une page de détail pour l'afficher par-dessus une page de fond.
//
// ─── Principe ───
//
// App.tsx rend DEUX jeux de routes : la page de fond à son propre emplacement,
// et la fiche par-dessus, enveloppée ici. Le défilement, la recherche et les
// filtres de la liste survivent donc à l'aller-retour, la liste n'étant jamais
// démontée.
//
// ⚠️ Monté uniquement au-delà de 768px (décision prise dans App.tsx via
//    useEstBureau). Sur mobile, la fiche reste une page pleine.
//
// ─── D'où vient le fond ───
//
// Deux origines, traitées pareil à l'affichage mais PAS à la fermeture :
//
//   1. Navigation depuis une liste → `state.fond` porte la page de départ.
//      Fermer = `navigate(-1)`, on rend l'entrée d'historique.
//
//   2. Lien direct (mail, favori, URL collée dans Teams) → aucun état.
//      App.tsx synthétise la liste de rattachement comme fond.
//      Fermer = `navigate(<liste>, { replace: true })` : il n'y a PAS d'entrée
//      d'historique derrière, un `navigate(-1)` ferait sortir du site.
//
// C'est le rôle de la prop `retourVers` : présente = fond synthétisé.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate, type Location } from 'react-router-dom'
import { ChevronLeft, X } from 'lucide-react'
import type { SuiteProblemes } from '@/utils/suiteProblemes'
import { useSuiteListe } from './SuiteProblemes'
import { useClicFond } from '@/hooks/useClicFond'

interface ValeurContexteModale {
  dansModale: boolean
  /** Page de fond courante, à propager aux navigations internes. */
  fond: Location | null
  /**
   * Rang de la fiche dans la chaîne, 0 pour la première ouverte au-dessus du
   * fond. Sert à savoir s'il y a un « précédent » à proposer, et de combien
   * d'entrées reculer pour tout refermer d'un coup.
   */
  profondeur: number
  /**
   * Chemin de repli quand la chaîne est partie d'un lien direct — il n'y a
   * alors pas d'entrée d'historique derrière la première fiche. Propagé de
   * fiche en fiche, sinon la deuxième ne saurait plus d'où vient la chaîne et
   * un retour arrière ferait sortir du site.
   */
  retourVers: string | null
  /**
   * Point d'ancrage sur le VOILE, hors du panneau : ce qu'une fiche y place
   * (flèches « problème suivant ») flotte de part et d'autre de la fenêtre.
   */
  ancreVoile: HTMLElement | null
  /**
   * Volet de travail À DROITE, dans la même fenêtre (06/10, déclaration NC-S) :
   * la fenêtre s'élargit et se divise, la fiche reste à gauche pour référence.
   * `volet` : où rendre le contenu (portail) ; `ouvrirVolet` : l'ouvrir / le fermer.
   */
  volet: HTMLElement | null
  ouvrirVolet: (ouvert: boolean) => void
  /** Le volet est ouvert : la fiche se réduit à une colonne (référence). */
  voletOuvert: boolean
  /** Mention au-dessus de la fiche réduite (défaut : « Le problème — pour référence »). */
  nommerReference: (libelle: string | null) => void
}

const ContexteModale = createContext<ValeurContexteModale>({
  dansModale: false,
  fond: null,
  profondeur: 0,
  retourVers: null,
  ancreVoile: null,
  volet: null,
  ouvrirVolet: () => undefined,
  voletOuvert: false,
  nommerReference: () => undefined,
})

/** État de navigation déposé par `useOuvrirFiche` sur l'entrée d'historique. */
export interface EtatModale {
  fond?: Location
  profondeur?: number
  retourVers?: string
  /** Liste d'où l'on a ouvert un problème, figée au clic (« Problème suivant »). */
  suite?: SuiteProblemes
  /** Événement de l'activité cliqué : la fiche marque les champs qu'il a touchés. */
  evenement?: string
  /** Arrivée par « + › Déclarer une NC-S » (06/10) : la fiche ouvre le formulaire NC-S. */
  ouvrirNcs?: boolean
  /** Arrivée par « + › Une FNC-T » ou « + Nouvelle FNC-T » (15/10) : la fiche équipement ouvre la FNC-T. */
  ouvrirFnct?: boolean
  /** Arrivée par Clore / Reporter de « Mes FNC-T » (22/10) : la fiche FNC-T ouvre ce geste sur cette action. */
  actionFnct?: { id: string; geste: 'clore' | 'reporter' }
  /** Arrivée par « Clore… » de la revue (22/10) : la fiche FNC-T ouvre la clôture (si l'on a le droit de clore). */
  clotureFnct?: boolean
}

/** Vrai si la page est rendue en surimpression plutôt qu'en page pleine. */
export function useDansModale(): boolean {
  return useContext(ContexteModale).dansModale
}

/** Ancre sur le voile de la fiche en surimpression, pour un portail ; null ailleurs. */
export function useAncreVoile(): HTMLElement | null {
  return useContext(ContexteModale).ancreVoile
}

/** Le volet de droite de la fenêtre (null hors surimpression). */
export function useVoletModale(): {
  volet: HTMLElement | null
  ouvrirVolet: (ouvert: boolean) => void
  voletOuvert: boolean
  nommerReference: (libelle: string | null) => void
} {
  const { volet, ouvrirVolet, voletOuvert, nommerReference } = useContext(ContexteModale)
  return { volet, ouvrirVolet, voletOuvert, nommerReference }
}

/**
 * Place à réserver à droite de la première ligne d'une fiche en surimpression,
 * pour ne pas passer sous les boutons de la fenêtre : la croix seule (56 px),
 * ou la croix et « fiche précédente » quand on a enchaîné (100 px). 0 en page
 * pleine.
 */
export function useMargeBoutonsModale(): number {
  const { dansModale, profondeur } = useContext(ContexteModale)
  if (!dansModale) return 0
  return profondeur > 0 ? 100 : 56
}

/**
 * Ouvre une fiche en surimpression depuis n'importe où.
 *
 * ⚠️ Depuis une modale, on propage le fond EXISTANT au lieu d'empiler : les
 * modales se remplacent au-dessus de la même liste. Empiler deux voiles
 * assombrirait l'écran deux fois et rendrait la fermeture ambiguë. Le bouton
 * Précédent reparcourt quand même la chaîne, chaque étape ayant son entrée
 * d'historique.
 *
 * « Problème suivant » (01/10) : un problème ouvert depuis une liste emporte
 * la suite de cette liste — celle passée en argument, sinon celle du
 * `SuiteListe` qui entoure l'appelant.
 *
 * `evenement` (02/10) : l'ID de l'événement d'activité d'où l'on vient — la fiche
 * pose une pastille « Modifié » sur les champs qu'il a changés.
 */
export function useOuvrirFiche(): (chemin: string, suite?: SuiteProblemes, evenement?: string, autres?: Pick<EtatModale, 'actionFnct' | 'clotureFnct'>) => void {
  const navigate = useNavigate()
  const location = useLocation()
  const { dansModale, fond, profondeur, retourVers } = useContext(ContexteModale)
  const suiteListe = useSuiteListe()

  return useCallback(
    (chemin: string, suite?: SuiteProblemes, evenement?: string, autres?: Pick<EtatModale, 'actionFnct' | 'clotureFnct'>) => {
      const etat: EtatModale = {
        fond: fond ?? location,
        // Depuis une liste on démarre à 0 ; depuis une fiche on avance d'un cran.
        profondeur: dansModale ? profondeur + 1 : 0,
      }
      if (retourVers) etat.retourVers = retourVers
      const suiteOrigine = suite ?? suiteListe
      if (suiteOrigine && chemin.startsWith('/issues/')) etat.suite = suiteOrigine
      if (evenement) etat.evenement = evenement
      if (autres?.actionFnct) etat.actionFnct = autres.actionFnct
      if (autres?.clotureFnct) etat.clotureFnct = true
      navigate(chemin, { state: etat })
    },
    [navigate, location, dansModale, fond, profondeur, retourVers, suiteListe],
  )
}

export interface ModaleRouteProps {
  children: ReactNode
  /** Page de fond, pour la propager aux navigations internes. */
  fond: Location
  /**
   * Chemin de repli quand le fond a été synthétisé (lien direct) : il n'y a
   * pas d'entrée d'historique à rendre. Absent = navigation normale.
   */
  retourVers?: string
  /**
   * Fenêtre large (1180 px) : la fiche FNC-T, document de travail dont le plan
   * d'actions ne tient pas dans 720 px (07/10, maquette de la refonte).
   */
  large?: boolean
}

// ─── Une fiche est-elle ouverte en surimpression ? (28/10) ───
//
// La page de fond (rendue avec la location du fond) ne voit pas l'adresse de
// la fiche : ce compteur le lui dit. Le récap « retour de vacances » se range à
// côté de la fiche tant qu'elle est ouverte, et revient au centre après.

let fichesOuvertes = 0
const abonnesFiches = new Set<() => void>()
function compterFiche(delta: number): void {
  fichesOuvertes += delta
  for (const f of abonnesFiches) f()
}
function abonnerFiches(f: () => void): () => void {
  abonnesFiches.add(f)
  return () => abonnesFiches.delete(f)
}
export function useFicheEnSurimpression(): boolean {
  return useSyncExternalStore(abonnerFiches, () => fichesOuvertes > 0, () => false)
}

export function ModaleRoute({
  children,
  fond,
  retourVers,
  large = false,
}: ModaleRouteProps): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    compterFiche(1)
    return () => compterFiche(-1)
  }, [])

  const etat = (location.state ?? {}) as EtatModale
  const profondeur = etat.profondeur ?? 0
  // Le repli peut venir de App.tsx (fond synthétisé pour CETTE fiche) ou avoir
  // été propagé depuis le début d'une chaîne partie d'un lien direct.
  const repli = retourVers ?? etat.retourVers ?? null

  // ─── Deux gestes distincts, qui étaient confondus ───
  //
  // La croix faisait `navigate(-1)`. Dans une chaîne liste → projet → chef,
  // elle ne fermait donc pas : elle reculait d'un cran, sur la fiche
  // précédente. Un bouton libellé « Fermer » qui rouvre autre chose.
  //
  //   ←  recule d'UNE fiche, et n'apparaît que s'il y en a une derrière
  //   ✕  referme TOUTE la chaîne et rend la main au fond

  const reculer = useCallback(() => navigate(-1), [navigate])

  const fermer = useCallback(() => {
    // Chaîne partie d'un lien direct : pas d'entrée d'historique à rendre,
    // reculer ferait sortir du site.
    if (repli) {
      navigate(repli, { replace: true })
      return
    }
    // Sinon on rend exactement les entrées empilées par la chaîne, ce qui
    // laisse un historique propre — le fond redevient l'entrée courante.
    navigate(-(profondeur + 1))
  }, [navigate, repli, profondeur])

  // Une fiche en remplace une autre sous la même fenêtre (problème suivant,
  // fiche sœur) : la nouvelle s'ouvre en haut, pas au défilement de la
  // précédente. Le voile est le conteneur qui défile.
  const voileRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    voileRef.current?.scrollTo({ top: 0 })
  }, [location.pathname])

  useEffect(() => {
    function surTouche(e: KeyboardEvent): void {
      if (e.key === 'Escape') fermer()
    }
    window.addEventListener('keydown', surTouche)
    return () => window.removeEventListener('keydown', surTouche)
  }, [fermer])

  // Le fond ne doit pas défiler pendant que la modale est ouverte.
  useEffect(() => {
    const precedent = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = precedent
    }
  }, [])

  // Un état et non une ref : la fiche doit être re-rendue une fois l'ancre
  // posée, pour y monter son portail.
  const [ancreVoile, setAncreVoile] = useState<HTMLDivElement | null>(null)
  // Volet de droite (déclaration NC-S, 06/10) : la fenêtre s'élargit et se divise.
  const [volet, setVolet] = useState<HTMLDivElement | null>(null)
  const [voletOuvert, ouvrirVolet] = useState(false)
  // La fiche équipement ouvre un problème dans le volet (10/10) : c'est alors
  // l'équipement qui reste « pour référence ».
  const [libelleReference, nommerReference] = useState<string | null>(null)

  const valeur = useMemo<ValeurContexteModale>(
    () => ({ dansModale: true, fond, profondeur, retourVers: repli, ancreVoile, volet, ouvrirVolet, voletOuvert, nommerReference }),
    [fond, profondeur, repli, ancreVoile, volet, voletOuvert],
  )

  // Le fond ne ferme que si le clic y a commencé (sélection glissée hors du cadre).
  const fond_ = useClicFond(fermer)

  return (
    <div
      ref={voileRef}
      className="modale-route-overlay"
      data-volet={voletOuvert}
      {...fond_}
      role="presentation"
    >
      <div ref={setAncreVoile} className="modale-route-ancre" />
      <div
        className="modale-route-panel"
        data-volet={voletOuvert}
        data-large={large}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modale-route-contenu">
        {voletOuvert && <p className="modale-route-reference">{libelleReference ?? 'Le problème — pour référence'}</p>}
        {profondeur > 0 && (
          <button
            type="button"
            className="modale-route-retour"
            onClick={reculer}
            aria-label="Revenir à la fiche précédente"
            title="Revenir à la fiche précédente"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        <button
          type="button"
          className="modale-route-fermer"
          onClick={fermer}
          aria-label="Fermer"
          title="Fermer"
        >
          <X size={18} />
        </button>
        <ContexteModale.Provider value={valeur}>{children}</ContexteModale.Provider>
        </div>
        <div ref={setVolet} className="modale-route-volet" />
      </div>
    </div>
  )
}
